import React from 'react';
import './Portfolio.css';
import unoLogo from '../assets/uno_logo.png';
import samsungLogo from '../assets/samsung_logo.png';

const PUBLICATIONS = [
  {
    status: 'Conference',
    year: '2025',
    title:
      'A Practice-Driven Framework for Transitioning Drive-by-Wire to Autonomous Driving Systems: A Case Study With a Chrysler Pacifica Hybrid Vehicle',
    authors: 'D. Zhang, C.-H. Ho, M. R. Islam, and P.-C. Huang',
    venue:
      'Proceedings of the 2025 International Conference on Automation Technology (Automation), Kaohsiung, Taiwan, pp. 1–6.',
    link: 'https://doi.org/10.1109/Automation66991.2025.11390021',
    linkLabel: 'DOI',
  },
  {
    status: 'Conference',
    year: '2024',
    title: 'Characterizing Encrypted Application Traffic Through Cellular Radio Interface Protocol',
    authors: 'M. R. Islam, R. H. Anwar, S. Mastorakis, and M. T. Raza',
    venue:
      '2024 IEEE 21st International Conference on Mobile Ad-Hoc and Smart Systems (MASS), Seoul, Korea, Republic of, pp. 321–329.',
    link: 'https://doi.org/10.1109/MASS62177.2024.00050',
    linkLabel: 'DOI',
  },
  {
    status: 'Journal',
    year: '2025',
    title: 'Image-based Deep Learning for Smart Digital Twins: A Review',
    authors: 'M. R. Islam, M. Subramaniam, and P.-C. Huang',
    venue: 'Artificial Intelligence Review, vol. 58, no. 5, p. 146.',
    link: 'https://doi.org/10.1007/s10462-024-11002-y',
    linkLabel: 'DOI',
  },
  {
    status: 'Conference',
    year: '2021',
    title: 'AllerHybrid: A Hybrid System to Predict the Allergen Using K-mer and Physicochemical Properties',
    authors: 'M. R. Islam, M. A. Kashem, and L. Mia',
    venue:
      '2021 5th International Conference on Electrical Engineering and Information Communication Technology (ICEEICT), pp. 1–6.',
    link: 'https://doi.org/10.1109/ICEEICT53905.2021.9667943',
    linkLabel: 'DOI',
  },
  {
    status: 'Accepted',
    year: '2026',
    title:
      'GenRL-CC: A GAN-Augmented Deep Reinforcement Learning Framework for Adaptive Congestion Control in Real-Time Communication',
    authors: 'M. R. Islam, X. Zhang, and P.-C. Huang',
    venue:
      'Proceedings of the 35th International Conference on Computer Communications and Networks (ICCCN), Honolulu, HI, USA.',
  },
  {
    status: 'Submitted',
    year: '2026',
    title:
      'Detecting Supraglacial Streams and Water Bodies in Southwest Greenland Using Drone Imagery and Deep Learning',
    authors: 'M. A. Hassan, A. Rennermalm, M. R. Islam, S. Leidman, and P.-C. Huang',
    venue:
      'Submitted to Journal of Geophysical Research: Machine Learning and Computation.',
  },
];

const ABSTRACTS = [
  {
    status: 'Poster',
    year: '2025',
    title: 'Detecting Supraglacial Streams in Southwest Greenland Using Drone Imagery and Deep Learning',
    authors: 'Md Arafat Hassan, Asa K. Rennermalm, and M. R. Islam',
    venue:
      'American Geophysical Union (AGU) Fall Meeting 2025, New Orleans, LA, USA, Dec. 17, 2025. Abstract ID: C31C-0942.',
    link: 'https://agu.confex.com/agu/agu25/meetingapp.cgi/Paper/1932832',
    linkLabel: 'Abstract Link',
    note:
      'Peer-reviewed abstract selected for presentation at a major international conference in Earth and environmental sciences.',
  },
];

const AWARDS = [
  {
    title: 'Graduate Research and Creative Activity (GRACA) Grant',
    organization: 'University of Nebraska Omaha',
    year: '2024–2025',
    description: 'Awarded $5,000 to support research on autonomous vehicles.',
    logo: unoLogo,
  },
  {
    title: 'Graduate Research and Creative Activity (GRACA) Grant',
    organization: 'University of Nebraska Omaha',
    year: '2023–2024',
    description: 'Awarded $5,000 to support research on real-time communication congestion control.',
    logo: unoLogo,
  },
  {
    title: 'SRBD Icon of the Month',
    organization: 'Samsung R&D Institute Bangladesh',
    year: 'March–April 2020',
    description: 'Recognized for contributions to the Samsung SmartThings project.',
    logo: samsungLogo,
  },
];

function Portfolio() {
  return (
    <section id="portfolio" className="site-section" aria-labelledby="portfolio-heading">
      <div className="site-section-inner portfolio-shell">
        <div className="section-heading">
          <p className="section-kicker">Publications</p>
          <h2 id="portfolio-heading" className="section-title">Research Output and Recognition</h2>
          <p className="section-intro">
            Peer-reviewed conference and journal publications across networking,
            digital twins, autonomous systems, and applied machine learning.
          </p>
        </div>

        <div className="portfolio-stack">
          <div className="portfolio-subsection">
            <div className="portfolio-subheading">
              <h3>Selected Publications</h3>
              <span>{PUBLICATIONS.length} entries</span>
            </div>

            <div className="portfolio-list">
              {PUBLICATIONS.map((item) => (
                <article key={`${item.title}-${item.year}`} className="publication-card card-surface">
                  <div className="publication-topline">
                    <span className="eyebrow-chip">{item.status}</span>
                    <span className="publication-year">{item.year}</span>
                  </div>
                  <h4>{item.title}</h4>
                  <p className="publication-authors">{item.authors}</p>
                  <p className="publication-venue">{item.venue}</p>
                  {item.link && (
                    <a href={item.link} target="_blank" rel="noopener noreferrer" className="action-button publication-link">
                      {item.linkLabel || 'View'}
                    </a>
                  )}
                </article>
              ))}
            </div>
          </div>

          <div className="portfolio-subsection">
            <div className="portfolio-subheading">
              <h3>Peer-Reviewed Conference Abstracts</h3>
              <span>{ABSTRACTS.length} entry</span>
            </div>

            <div className="portfolio-list">
              {ABSTRACTS.map((item) => (
                <article key={item.title} className="publication-card card-surface">
                  <div className="publication-topline">
                    <span className="eyebrow-chip">{item.status}</span>
                    <span className="publication-year">{item.year}</span>
                  </div>
                  <h4>{item.title}</h4>
                  <p className="publication-authors">{item.authors}</p>
                  <p className="publication-venue">{item.venue}</p>
                  {item.note && <p className="publication-note">{item.note}</p>}
                  {item.link && (
                    <a href={item.link} target="_blank" rel="noopener noreferrer" className="action-button publication-link">
                      {item.linkLabel || 'View'}
                    </a>
                  )}
                </article>
              ))}
            </div>
          </div>

          <div className="portfolio-subsection">
            <div className="portfolio-subheading">
              <h3>Awards and Recognition</h3>
              <span>{AWARDS.length} honors</span>
            </div>

            <div className="awards-grid">
              {AWARDS.map((award) => (
                <article key={`${award.title}-${award.year}`} className="award-card card-surface">
                  <div className="award-header">
                    <div className="award-logo-shell">
                      <img src={award.logo} alt={`${award.organization} logo`} className="award-logo" />
                    </div>
                    <div>
                      <h4>{award.title}</h4>
                      <p>{award.organization}</p>
                    </div>
                  </div>
                  <span className="eyebrow-chip">{award.year}</span>
                  <p className="award-description">{award.description}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Portfolio;
